import type { ImgHTMLAttributes } from 'react'

const ASSETS = '/assets'

type ResponsiveImageProps = {
  /** Base name without extension (e.g. 'attorney-7' or 'AdobeStock_844165661-scaled') */
  name: string
  alt: string
  className?: string
  /** Eager for above-the-fold (hero, logo), lazy for below */
  loading?: 'lazy' | 'eager'
  /** For large images we have -400w, -800w, -1200w.webp. Omit for small assets (single .webp). */
  responsive?: boolean
  /** Original extension for fallback (jpg, jpeg, png) */
  fallbackExt?: 'jpg' | 'jpeg' | 'png'
  /** If true, only use fallback image (no &lt;picture&gt;/webp). Use when webp variant does not exist. */
  fallbackOnly?: boolean
  sizes?: string
  /** Hint for LCP image (hero). Use "high" only for the main LCP element. */
  fetchPriority?: 'high' | 'low' | 'auto'
  /** Intrinsic width for aspect-ratio / CLS (optional). */
  width?: number
  /** Intrinsic height for aspect-ratio / CLS (optional). */
  height?: number
}

const WIDTHS = [320, 400, 800, 1200] as const

function imgProps(
  alt: string,
  loading: ResponsiveImageProps['loading'],
  className: string,
  fetchPriority?: ResponsiveImageProps['fetchPriority'],
  width?: number,
  height?: number,
  sizes?: string
): ImgHTMLAttributes<HTMLImageElement> {
  const p: ImgHTMLAttributes<HTMLImageElement> = {
    alt,
    loading,
    decoding: 'async',
    className,
    ...(fetchPriority && fetchPriority !== 'auto' && { fetchPriority }),
    ...(width != null && { width }),
    ...(height != null && { height }),
    ...(sizes && { sizes }),
  }
  return p
}

export function ResponsiveImage({
  name,
  alt,
  className = '',
  loading = 'lazy',
  responsive = false,
  fallbackExt = 'jpg',
  fallbackOnly = false,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
  fetchPriority,
  width,
  height,
}: ResponsiveImageProps) {
  const fallbackSrc = `${ASSETS}/${name}.${fallbackExt}`

  if (fallbackOnly) {
    return (
      <img
        src={fallbackSrc}
        {...imgProps(alt, loading, className, fetchPriority, width, height)}
      />
    )
  }

  if (responsive) {
    const srcSetWebP = WIDTHS.map((w) => `${ASSETS}/${name}-${w}w.webp ${w}w`).join(', ')
    return (
      <picture>
        <source type="image/webp" srcSet={srcSetWebP} sizes={sizes} />
        <img
          src={fallbackSrc}
          {...imgProps(alt, loading, className, fetchPriority, width, height, sizes)}
        />
      </picture>
    )
  }

  const webpSrc = `${ASSETS}/${name}.webp`
  return (
    <picture>
      <source type="image/webp" srcSet={webpSrc} />
      <img
        src={fallbackSrc}
        {...imgProps(alt, loading, className, fetchPriority, width, height)}
      />
    </picture>
  )
}
